// Angular
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// RxJS
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';
// Translate
import { TranslateService } from '@ngx-translate/core';
// Store
import { Store } from '@ngrx/store';
import { AppState } from '../../../../core/reducers';
// Auth
import { AuthNoticeService, AuthService, Login } from '../../../../core/auth';
import { AuthenticateService } from '@app/api/authenticate.service';
import { environment } from '../../../../../environments/environment';

import { CookieService } from 'ngx-cookie-service';

/**
 * ! Just example => Should be removed in development
 */

@Component({
  selector: 'kt-login',
  templateUrl: './login.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class LoginComponent implements OnInit, OnDestroy {
  // Public params
  loginForm: FormGroup;
  loading = false;
  isLoggedIn$: Observable<boolean>;
  errors: any = [];

  private unsubscribe: Subject<any>;

  private returnUrl: any;

  // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

  /**
   * Component constructor
   *
   * @param router: Router
   * @param auth: AuthService
   * @param authNoticeService: AuthNoticeService
   * @param translate: TranslateService
   * @param store: Store<AppState>
   * @param fb: FormBuilder
   * @param cdr
   * @param route
   */
  constructor(
    private router: Router,
    private auth: AuthService,
    private authNoticeService: AuthNoticeService,
    private translate: TranslateService,
    private store: Store<AppState>,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private authenticateService: AuthenticateService,
    private cookieService: CookieService
  ) {
    this.unsubscribe = new Subject();
  }

  /**
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */
  ngOnInit(): void {
    this.initLoginForm();

    // redirect back to the returnUrl before login
    this.route.queryParams.subscribe((params) => {
      this.returnUrl = params.returnUrl || '/';
    });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this.authNoticeService.setNotice(null);
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.loading = false;
  }

  /**
   * Form initalization
   * Default params, validators
   */
  initLoginForm() {
    // demo message to show
    if (!this.authNoticeService.onNoticeChanged$.getValue()) {
      const initialNotice = `Enter your account login`;
      this.authNoticeService.setNotice(initialNotice, 'info');
    }

    this.loginForm = this.fb.group({
      email: [
        '',
        Validators.compose([
          Validators.required,
          Validators.email,
          Validators.minLength(3),
          Validators.maxLength(320), // https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
        ]),
      ],
      password: [
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
        ]),
      ],
    });
  }

  /**
   * Form Submit
   */
  submit() {
    const controls = this.loginForm.controls;
    /** check form */
    if (this.loginForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      return;
    }

    this.loading = true;

    const authData = {
      email: controls.email.value,
      password: controls.password.value,
    };

    this.authenticateService
      .apiAuthenticateAuthenticatePost(authData.email, authData.password)
      .pipe(
        tap((res) => {
          console.log('[AUTH] Login response:', JSON.stringify(res));
          if (res && res.success) {
            const token =
              res.token ||
              (res.data && res.data.token) ||
              res.authToken ||
              res.accessToken ||
              (res.data && res.data.authToken) ||
              (res.data && res.data.accessToken);
            
            console.log('[AUTH] Token extracted:', token ? 'YES' : 'NO');
            
            if (!token) {
              console.error('[AUTH] Token not found in response keys:', Object.keys(res), res.data ? Object.keys(res.data) : 'no data');
              this.authNoticeService.setNotice('Login succeeded but token missing', 'danger');
              return;
            }
            localStorage.setItem(environment.authTokenKey, token);
            console.log('[AUTH] ✅ Token saved, reloading to init auth state...');
            // Reload trang để init$ đọc token → load user + roles từ JWT
            // Đảm bảo toàn bộ header/components cập nhật đúng trạng thái đăng nhập
            window.location.href = this.returnUrl || '/';
          } else {
            this.authNoticeService.setNotice(
              res && res.error ? res.error : 'Login failed',
              'danger'
            );
          }
        }),
        takeUntil(this.unsubscribe),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe();
    // this.auth
    // 	.login(authData.email, authData.password)
    // 	.pipe(
    // 		tap(user => {
    // 			if (user) {
    // 				this.store.dispatch(new Login({authToken: user.accessToken}));
    // 				this.router.navigateByUrl(this.returnUrl); // Main page
    // 			} else {
    // 				this.authNoticeService.setNotice(this.translate.instant('AUTH.VALIDATION.INVALID_LOGIN'), 'danger');
    // 			}
    // 		}),
    // 		takeUntil(this.unsubscribe),
    // 		finalize(() => {
    // 			this.loading = false;
    // 			this.cdr.markForCheck();
    // 		})
    // 	)
    // 	.subscribe();
  }

  /**
   * Checking control validation
   *
   * @param controlName: string => Equals to formControlName
   * @param validationType: string => Equals to valitors name
   */
  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.loginForm.controls[controlName];
    if (!control) {
      return false;
    }

    const result = control.hasError(validationType) && (control.dirty || control.touched);
    return result;
  }
}
